import { styled } from '@mui/material/styles';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';

// Main game container
export const GameContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  maxWidth: '800px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down('xs')]: {
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
}));

// Game card wrapper
export const GameCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
}));

// Game content
export const GameContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.down('xs')]: {
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
}));

// Game title
export const GameTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '1.3rem',
  },
}));

// Game subtitle
export const GameSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontStyle: 'italic',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '0.8rem',
  },
}));

// Word display container
export const WordDisplayContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: '60px',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${theme.palette.primary.light}`,
  [theme.breakpoints.down('sm')]: {
    minHeight: '40px',
    padding: theme.spacing(1),
    gap: theme.spacing(0.25),
  },
  [theme.breakpoints.down('xs')]: {
    minHeight: '35px',
    padding: theme.spacing(0.75),
    gap: theme.spacing(0.25),
  },
}));

// Letter display
export const LetterDisplay = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isGuessed',
})(({ theme, isGuessed }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  color: isGuessed ? theme.palette.primary.main : theme.palette.text.primary,
  minWidth: '30px',
  textAlign: 'center',
  borderBottom: isGuessed ? 'none' : `3px solid ${theme.palette.primary.main}`,
  padding: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
    minWidth: '20px',
    padding: theme.spacing(0.25),
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '1rem',
    minWidth: '18px',
    padding: theme.spacing(0.25),
  },
}));

// Stick figure container
export const StickFigureContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${theme.palette.secondary.main}`,
  [theme.breakpoints.down('sm')]: {
    minHeight: '180px',
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down('xs')]: {
    minHeight: '160px',
    padding: theme.spacing(1),
  },
}));

// Keyboard container
export const KeyboardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
  },
  '@media (max-width: 400px)': {
    gap: '4px',
  },
}));

// Keyboard row
export const KeyboardRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.25),
  },
  '@media (max-width: 400px)': {
    gap: '2px',
  },
}));

// Letter button
export const LetterButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isGuessed' && prop !== 'isCorrect',
})(({ theme, isGuessed, isCorrect }) => ({
  minWidth: '40px',
  height: '40px',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  backgroundColor: isGuessed
    ? isCorrect
      ? theme.palette.success.main
      : theme.palette.error.main
    : theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: isGuessed
      ? isCorrect
        ? theme.palette.success.dark
        : theme.palette.error.dark
      : theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: isGuessed
      ? isCorrect
        ? theme.palette.success.main
        : theme.palette.error.main
      : theme.palette.grey[400],
    color: 'white',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '32px',
    height: '32px',
    fontSize: '0.9rem',
  },
  [theme.breakpoints.down('xs')]: {
    minWidth: '28px',
    height: '28px',
    fontSize: '0.8rem',
  },
  '@media (max-width: 400px)': {
    minWidth: '24px',
    height: '24px',
    fontSize: '0.7rem',
    padding: '2px',
  },
}));

// Game status display
export const GameStatusDisplay = styled(Typography, {
  shouldForwardProp: prop => prop !== 'status',
})(({ theme, status }) => ({
  fontSize: '1.2rem',
  fontWeight: 'bold',
  textAlign: 'center',
  color: theme.palette.text.primary,
  padding: theme.spacing(2),
  backgroundColor:
    status === 'won'
      ? theme.palette.success.light
      : status === 'lost'
        ? theme.palette.error.light
        : 'transparent',
  borderRadius: theme.shape.borderRadius,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1),
  },
}));

// New game button
export const NewGameButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  fontWeight: 'bold',
  padding: theme.spacing(1.5, 3),
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2),
    fontSize: '0.9rem',
  },
  [theme.breakpoints.down('xs')]: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem',
  },
}));

// Guesses counter
export const GuessesCounter = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '0.75rem',
  },
}));
